import { HydrationBoundary, QueryClient, QueryClientProvider, type DehydratedState } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";
import { AuthProvider } from "./auth";

export function createQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 60_000,
        retry: (failureCount, error) => (error as Error & { status?: number })?.status !== 404 && failureCount < 1,
        refetchOnWindowFocus: false,
      },
      mutations: { retry: false },
    },
  });
}

export function AppProviders({ children, client: providedClient, dehydratedState }: { children: ReactNode; client?: QueryClient; dehydratedState?: DehydratedState }) {
  const [client] = useState(() => providedClient || createQueryClient());
  return (
    <QueryClientProvider client={client}>
      <HydrationBoundary state={dehydratedState}>
        <AuthProvider>{children}</AuthProvider>
      </HydrationBoundary>
    </QueryClientProvider>
  );
}

export const queryKeys = {
  home: ["public", "home"] as const,
  pricing: ["public", "pricing"] as const,
  categories: ["public", "categories"] as const,
  search: (filters: unknown) => ["public", "search", filters] as const,
  ranking: (period: string, category?: string) => ["public", "ranking", period, category] as const,
  creator: (slug: string) => ["public", "creator", slug] as const,
  profile: ["me", "profile"] as const,
  referrals: ["me", "referrals"] as const,
  entitlements: ["me", "entitlements"] as const,
  overview: ["me", "overview"] as const,
  links: ["me", "links"] as const,
  analytics: (period: string) => ["me", "analytics", period] as const,
  score: ["me", "score"] as const,
  featured: ["me", "featured"] as const,
  admin: ["admin", "overview"] as const,
};
